/**
 * Banners promocionais da home da Loja (Plano Loja/Twotrees, Fase 9).
 *
 * Mesmo padrão de `MarketingFaixaPresale.tsx`: toggle master (feature flag
 * `banners`, 0046) + cadastro de banners (imagem + link opcional + ordem +
 * janela opcional de datas) — a Loja lê os dois via
 * `storefront-marketing-runtime` e reflete sem redeploy (GET
 * /api/marketing/banners). Diferença de propósito: banner não está amarrado
 * a um produto do catálogo (é imagem institucional/promocional), então o
 * formulário pede URL de imagem (já hospedada — sem storage no projeto) e
 * texto alternativo obrigatório em vez de um seletor de produto.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca de sintaxe
 * (`createElement` → JSX) + ícone `Image` no título, no mesmo padrão visual
 * de `Cupons.tsx` (ícone + texto no `h2`, nunca só o texto sozinho — régua
 * §2). Esta tela não tinha nenhum ícone antes (lacuna real). Nenhum
 * `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `Margens.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Image } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import {
  useAddBanner,
  useBanners,
  useMarketingFeatureFlags,
  useRemoveBanner,
  useSetMarketingFeatureFlag,
  useUpdateBanner
} from "../queries.js";
import type { Banner, MarketingFeatureFlag } from "../api/types.js";

const CONTROL =
  "w-full rounded-control border border-border-strong bg-surface px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted focus-visible:outline-none aria-[invalid=true]:border-danger";

export interface BannerEditHandlers {
  add: (input: { imageUrl: string; altText: string; linkUrl: string | null }) => void;
  toggleActive: (banner: Banner) => void;
  remove: (id: string) => void;
  pending: boolean;
  error: Error | null;
}

export interface MarketingBannersViewProps {
  bannersFlag: MarketingFeatureFlag | null;
  onToggleFlag: (enabled: boolean) => void;
  flagPending: boolean;
  banners: Banner[];
  edit?: BannerEditHandlers;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface BannerFormValues {
  get(name: string): unknown;
}

/**
 * Caminho de submit REAL do formulário de adicionar banner, isolado do
 * evento de DOM. Devolve o input enviado, ou `null` quando a validação
 * recusa antes de chamar o servidor (URL de imagem/texto alternativo vazios)
 * — mesma convenção de `submitAddPresaleHighlightForm`.
 */
export function submitAddBannerForm(
  values: BannerFormValues,
  edit: BannerEditHandlers
): { imageUrl: string; altText: string; linkUrl: string | null } | null {
  const imageUrl = values.get("imageUrl");
  const altText = values.get("altText");
  const linkUrlRaw = values.get("linkUrl");
  if (typeof imageUrl !== "string" || !imageUrl.trim()) return null;
  if (typeof altText !== "string" || !altText.trim()) return null;
  const linkUrl = typeof linkUrlRaw === "string" && linkUrlRaw.trim() ? linkUrlRaw.trim() : null;
  const input = { imageUrl: imageUrl.trim(), altText: altText.trim(), linkUrl };
  edit.add(input);
  return input;
}

function FlagToggle({ flag, pending, onToggle }: { flag: MarketingFeatureFlag | null; pending: boolean; onToggle: (enabled: boolean) => void }): ReactElement {
  const enabled = flag?.enabled ?? false;
  return (
    <section className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1" data-section="banners-flag">
      <h3 className="text-lg font-extrabold text-ink">Banners da home</h3>
      <p className="page__note flex flex-wrap items-center gap-2 text-sm text-muted">
        <span>Status:</span>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
            (enabled ? "border-success text-success" : "border-border-strong text-body")
          }
          data-flag-enabled={enabled ? "on" : "off"}
        >
          {enabled ? "ligados" : "desligados"}
        </span>
      </p>
      <button
        type="button"
        disabled={pending || !flag}
        data-action="toggle-banners"
        className={
          "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control px-4 py-2 text-sm font-semibold " +
          "transition-colors duration-fast ease-s2 focus-visible:outline-none disabled:opacity-70 " +
          (enabled ? "border border-border-strong text-body hover:bg-surface-soft" : "bg-brand text-on-brand hover:bg-brand-pressed")
        }
        onClick={() => onToggle(!enabled)}
      >
        {pending ? "Salvando…" : enabled ? "Desligar banners" : "Ligar banners"}
      </button>
    </section>
  );
}

function BannerActions({ banner, edit }: { banner: Banner; edit?: BannerEditHandlers }): ReactElement | null {
  if (!edit) return null;
  const BTN =
    "inline-flex min-h-[32px] items-center justify-center rounded-control border border-border-strong px-2 text-xs font-semibold text-body hover:bg-surface-soft disabled:opacity-50";
  return (
    <div className="flex flex-wrap gap-1">
      <button type="button" className={BTN} disabled={edit.pending} data-action={`banner-toggle-${banner.id}`} onClick={() => edit.toggleActive(banner)}>
        {banner.active ? "Desativar" : "Ativar"}
      </button>
      <button
        type="button"
        className={`${BTN} border-danger text-danger`}
        disabled={edit.pending}
        data-action={`banner-remove-${banner.id}`}
        onClick={() => edit.remove(banner.id)}
      >
        Remover
      </button>
    </div>
  );
}

export function MarketingBannersView({ bannersFlag, onToggleFlag, flagPending, banners, edit }: MarketingBannersViewProps): ReactElement {
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edit) return;
    const form = event.currentTarget;
    const result = submitAddBannerForm(new FormData(form), edit);
    if (result) form.reset();
  }

  return (
    <div className="page page--marketing-banners flex flex-col gap-6" data-page="marketing-banners">
      <header className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-2xl font-extrabold text-ink">
          <Image className="h-5 w-5 shrink-0 text-brand-text" aria-hidden focusable={false} />
          Banners
        </h2>
        <p className="page__note text-sm text-muted">
          Cadastre banners promocionais da home e ligue/desligue a exibição. Sem redeploy: a Loja lê direto deste cadastro.
        </p>
      </header>
      <FlagToggle flag={bannersFlag} pending={flagPending} onToggle={onToggleFlag} />
      <RecordTable<Banner>
        caption="Banners cadastrados"
        rows={banners}
        idOf={(row) => row.id}
        emptyMessage="Nenhum banner cadastrado ainda."
        columns={[
          {
            key: "imageUrl",
            label: "Imagem",
            render: (row) => <img src={row.imageUrl} alt={row.altText} className="h-10 w-auto rounded-control object-cover" />
          },
          { key: "altText", label: "Texto alternativo", render: (row) => row.altText },
          { key: "linkUrl", label: "Link", render: (row) => row.linkUrl ?? "—" },
          {
            key: "active",
            label: "Situação",
            render: (row) => (
              <span
                className={
                  "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
                  (row.active ? "border-success text-success" : "border-border-strong text-body")
                }
                data-banner-active={row.active ? "yes" : "no"}
              >
                {row.active ? "ativo" : "inativo"}
              </span>
            )
          },
          { key: "position", label: "Ordem", align: "right", render: (row) => String(row.position) },
          { key: "actions", label: "Ações", render: (row) => <BannerActions banner={row} edit={edit} /> }
        ]}
      />
      {edit ? (
        <form className="banner-add-form flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1" data-form="add-banner" onSubmit={onSubmit}>
          <h4 className="text-sm font-semibold text-ink">Adicionar banner</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="banner-image-url" className="text-sm font-semibold text-ink">
                URL da imagem
              </label>
              <input
                id="banner-image-url"
                name="imageUrl"
                type="url"
                required
                disabled={edit.pending}
                className={CONTROL}
                placeholder="https://…/banner.jpg"
                data-field="imageUrl"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="banner-alt-text" className="text-sm font-semibold text-ink">
                Texto alternativo
              </label>
              <input
                id="banner-alt-text"
                name="altText"
                type="text"
                required
                disabled={edit.pending}
                className={CONTROL}
                placeholder="Promoção de outubro"
                data-field="altText"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="banner-link-url" className="text-sm font-semibold text-ink">
                Link (opcional)
              </label>
              <input
                id="banner-link-url"
                name="linkUrl"
                type="url"
                disabled={edit.pending}
                className={CONTROL}
                placeholder="https://…/promocao"
                data-field="linkUrl"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={edit.pending}
            data-action="add-banner"
            className={
              "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control bg-brand px-4 py-2 " +
              "text-sm font-semibold text-on-brand transition-colors duration-fast ease-s2 " +
              "hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-70"
            }
          >
            {edit.pending ? "Gravando…" : "Adicionar banner"}
          </button>
        </form>
      ) : (
        <StatusPanel kind="awaiting_source" title="Gravação de banner não montada nesta visualização" detail="A lista acima reflete o banco; esta montagem é só leitura." />
      )}
      {edit?.error ? <StatusPanel kind="error" title="O banner não foi atualizado" detail={edit.error.message} /> : null}
    </div>
  );
}

export function MarketingBanners(): ReactElement {
  const flagsQuery = useMarketingFeatureFlags();
  const bannersQuery = useBanners();
  const setFlag = useSetMarketingFeatureFlag();
  const addBanner = useAddBanner();
  const updateBanner = useUpdateBanner();
  const removeBanner = useRemoveBanner();

  if (flagsQuery.isPending || bannersQuery.isPending) {
    return <StatusPanel kind="loading" title="Carregando banners…" />;
  }
  if (flagsQuery.error || bannersQuery.error) {
    return <StatusPanel kind="error" title="Não foi possível carregar os banners" detail={errorDetail(flagsQuery.error ?? bannersQuery.error)} />;
  }

  const bannersFlag = (flagsQuery.data ?? []).find((f) => f.featureKey === "banners") ?? null;

  const pending = addBanner.isPending || updateBanner.isPending || removeBanner.isPending;
  const error = addBanner.error ?? updateBanner.error ?? removeBanner.error ?? null;

  return (
    <MarketingBannersView
      bannersFlag={bannersFlag}
      flagPending={setFlag.isPending}
      onToggleFlag={(enabled) => setFlag.mutate({ featureKey: "banners", enabled })}
      banners={bannersQuery.data ?? []}
      edit={{
        add: (input) => addBanner.mutate(input),
        toggleActive: (banner) => updateBanner.mutate({ id: banner.id, patch: { active: !banner.active } }),
        remove: (id) => removeBanner.mutate({ id }),
        pending,
        error
      }}
    />
  );
}
